/**
 * Reads the currently-visible session ID from the browser URL.
 *
 * Used by entity-verification flows (manual analyze + auto-verify) to detect
 * whether the user is still on the session whose pipeline just completed. A
 * React ref / hook would freeze at the value captured when the mutation fired;
 * the URL is always current, even after the triggering component unmounts.
 *
 * SSR-safe: returns an empty string when `window` is undefined.
 */
// Matches both the legacy `/session/:id` route and the new session-list
// `/sessions/:id` route, so auto-verify keeps resolving the active session
// once the new session list ships.
const SESSION_ROUTE_SEGMENT_PATTERN = /^sessions?$/;

/** Path prefix shared by every embedded EHR integration route (Epic Haiku, SMART on FHIR, …). */
const INTEGRATION_ROUTE_PREFIX = '/integration/';

// The Haiku complete page (`/integration/epic/haiku/complete/[...id]`) embeds
// the Heidi sessionId as the first catch-all segment — mirrors
// `useCurrentSessionId` in src/hooks/useCurrentIds.ts. Other integration
// routes carry no Heidi session id in the URL (e.g. the Haiku
// `[region]/[profile_name]/[external_session_id]` variant uses the EHR's
// external id), so they intentionally resolve to ''.
const HAIKU_COMPLETE_ROUTE_PREFIX = '/integration/epic/haiku/complete/';

/**
 * True when the app is rendered inside an embedded EHR integration context
 * (any `/integration/*` route). Cross-session navigation prompts must be
 * suppressed there — deep-linking to `/scribe/session/...` would pull the
 * clinician out of the EHR embed into the standalone web app.
 */
export function isIntegrationRoute(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith(INTEGRATION_ROUTE_PREFIX);
}

export function getActiveSessionIdFromUrl(): string {
  if (typeof window === 'undefined') return '';
  const { pathname } = window.location;

  if (pathname.startsWith(HAIKU_COMPLETE_ROUTE_PREFIX)) {
    return pathname.slice(HAIKU_COMPLETE_ROUTE_PREFIX.length).split('/')[0] ?? '';
  }

  const segments = pathname.split('/');
  const sessionIndex = segments.findIndex((segment) =>
    SESSION_ROUTE_SEGMENT_PATTERN.test(segment)
  );
  return sessionIndex >= 0 ? (segments[sessionIndex + 1] ?? '') : '';
}
